import React, { useEffect, useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlLimitedInput } from 'src/components/BeaconV2Components/SlLimitedInput';
import color from 'src/utils/colors';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import NumberFormat from 'react-number-format';
import CustomTextField from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/components/StyledTextField';
import { InputAdornment } from '@mui/material';
import { OptimizationDropdown } from '../components';
import PortfolioBuilderStore, { Error } from '../stores';
import EntityGrid from './EntityGrid';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { useSnackbar } from 'src/utils/Hooks';
import { formatMoney } from 'src/utils/stringFormatting';
import colors from 'src/utils/colors';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';

const PortfolioSettings = () => {
  const { showSnackbar } = useSnackbar();
  const currencySymbol = useAppSelector((state) => state?.retailer?.currencySymbol) || '$';
  const { selectedEntityId, budget, setBudget, portfolioName, setPortfolioName, error, setError } =
    PortfolioBuilderStore((s) => ({
      budget: s.budget,
      setBudget: s.setBudget,
      portfolioName: s.portfolioName,
      setPortfolioName: s.setPortfolioName,
      error: s.error,
      setError: s.setError,
      selectedEntityId: s.selectedEntityId
    }));

  useEffect(() => {
    if (error.length > 0) {
      showSnackbar({
        type: 'error',
        message: 'Please enter all of the required fields'
      });
    }
  }, [error, setError, showSnackbar]);

  const handleNext = useCallback(() => {
    const errors: Error[] = [];

    if (selectedEntityId === null) {
      errors.push(Error.Entity);
    }
    if (!budget || budget < 1) {
      errors.push(Error.Budget);
    }
    if (portfolioName === '') {
      errors.push(Error.Name);
    }

    errors.forEach((e) => setError(e));
    return errors.length === 0;
  }, [budget, portfolioName, setError, selectedEntityId]);

  const { beaconNavigator } = useBeaconNavigator();

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={32}>
        <Flex flexDirection="column" gap={8}>
          <Text variant="h7">Portfolio Name</Text>
          <SlLimitedInput
            placeholder="Name your portfolio"
            required
            onChange={(e) => setPortfolioName(e.target.value)}
            characterLimit={100}
            value={portfolioName}
            sx={{
              width: '100%',
              border: `1px solid ${error.includes(Error.Name) ? color.errorRed : color.graysSecondary} !important`,
              padding: '10px 14px',
              boxSizing: 'border-box',
              '& .MuiInputBase-root': {
                borderRadius: '4px',
                backgroundColor: '#fff',
                height: '48px',
                fontSize: '14px',
                fontWeight: 400
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: colors.grey,
                opacity: '0.5'
              },
              '& .MuiFormLabel-root': {
                color: color.blueGrey
              }
            }}
          />
        </Flex>
        <Flex
          flexDirection="column"
          gap={40}
          sx={{
            'td:first-child': {
              width: '15px !important'
            }
          }}
        >
          <Flex flexDirection="column" gap={8}>
            <Text variant="h7">Budget</Text>
            <Flex gap={12}>
              <NumberFormat
                customInput={CustomTextField}
                name="value"
                value={formatMoney(budget, '', 2)}
                onValueChange={({ floatValue }) => {
                  setBudget(floatValue);
                }}
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        '& .MuiTypography-root': {
                          color: color.darkBlue
                        }
                      }}
                    >
                      {currencySymbol}
                    </InputAdornment>
                  ),
                  sx: {
                    border: `1px solid ${
                      error.includes(Error.Budget) ? color.errorRed : color.graysSecondary
                      } !important`,
                    padding: '0px 8px !important',
                    'input::placeholder': {
                      color: color.blueGrey,
                      opacity: 1
                    },
                    input: {
                      width: '87px',
                      textAlign: 'right'
                    }
                  }
                }}
                decimalScale={2}
                fixedDecimalScale
                isNumericString
                allowNegative={false}
                required
              />
              <Text
                variant="body2"
                sx={{
                  height: 'fit-content',
                  marginTop: 'auto',
                  marginBottom: 'auto'
                }}
              >
                This will increase the selected entity budget by {currencySymbol}
                {formatMoney(budget ?? 0, '', 2)}
              </Text>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={8}>
            <Text variant="h7">Optimization</Text>
            <OptimizationDropdown />
          </Flex>
          <EntityGrid />
        </Flex>
      </Flex>
      <div
        style={{
          marginLeft: 'auto',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '64px'
        }}
      >
        <SlButton
          disabled
          buttonSx={{
            backgroundColor: 'white'
          }}
        >
          Back
        </SlButton>
        <SlButton
          variant="contained"
          onClick={() => {
            if (handleNext()) {
              beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'portfolioCampaigns' });
              beaconNavigator.go();
            }
          }}
        >
          Next
        </SlButton>
      </div>
    </BasePageContainer>
  );
};

export default PortfolioSettings;
